import React from "react";
import { IconExternalLink } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

const SharePalIcon = ({ compact }: { compact?: boolean }) => (
  <img
    src="https://images.sharepal.in/about-us/logo-p-blue.svg"
    alt="SharePal"
    className={compact ? "w-4 h-4" : "w-6 h-6"}
  />
);

interface LoginButtonProps {
  title: string;
  subtitle?: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'rent' | 'buy' | 'ticketing';
  className?: string;
  /** Compact style for header (no subtitle, smaller size) */
  compact?: boolean;
}

export const LoginButton: React.FC<LoginButtonProps> = ({
  title,
  subtitle,
  onClick,
  isLoading = false,
  disabled = false,
  variant = 'primary',
  className,
  compact = false,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'rent':
        return 'bg-rent hover:bg-rent/90';
      case 'buy':
        return 'bg-buy hover:bg-buy/90';
      case 'ticketing':
        return 'bg-[#3498db] hover:bg-[#3498db]/90';
      default:
        return 'bg-primary hover:bg-primary/90';
    }
  };

  const isDisabled = disabled || isLoading;
  const showSubtitle = !compact && subtitle;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl cursor-pointer transition-all duration-200",
        getVariantStyles(),
        isDisabled && "opacity-50 cursor-not-allowed",
        compact && "rounded-lg",
        className
      )}
      onClick={isDisabled ? undefined : onClick}
      style={compact
        ? { padding: '8px 12px 8px 10px', minWidth: '140px', minHeight: '36px' }
        : { padding: '16px 24px', minWidth: '220px', minHeight: '60px' }
      }
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>

      <div className={cn(
        "relative z-10 flex items-center justify-between gap-4",
        compact && "gap-2"
      )}>
        <div className={cn("bg-lime-300 rounded-lg shrink-0", compact ? "p-1" : "p-2")}>
          <SharePalIcon compact={compact} />
        </div>

        <div className="text-left flex-1 min-w-0 overflow-hidden">
          <div className={cn(
            "text-white font-bold truncate",
            compact ? "text-xs" : "text-lg mb-1"
          )}>
            {isLoading ? "CONNECTING..." : title}
          </div>
          {showSubtitle && (
            <div className="text-white/90 text-xs font-medium">
              {subtitle}
            </div>
          )}
        </div>

        <div className={cn("bg-white/20 rounded-lg shrink-0", compact ? "p-1" : "p-2")}>
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : (
            <IconExternalLink size={compact ? 14 : 18} className="text-white shrink-0" strokeWidth={2.5} />
          )}
        </div>
      </div>
    </div>
  );
};
